// =============================================================================
// fileHash.js, small helpers to compute a stable file fingerprint and check
// the backend for duplicate-upload protection.
//
// Phase 17, Statement Import v2
// =============================================================================
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compute SHA-256 of a File / Blob as lowercase hex.
 * Uses the browser's Web Crypto API, no external deps, no PII leaves the page.
 *
 * Falls back gracefully if SubtleCrypto isn't available (very old browsers /
 * insecure contexts): returns null so callers treat the file as "no hash".
 */
export async function sha256OfFile(file) {
  try {
    if (!file) return null;
    if (!window.crypto?.subtle) return null;
    const buf = await file.arrayBuffer();
    const digest = await window.crypto.subtle.digest('SHA-256', buf);
    const bytes = new Uint8Array(digest);
    let hex = '';
    for (let i = 0; i < bytes.length; i += 1) {
      hex += bytes[i].toString(16).padStart(2, '0');
    }
    return hex;
  } catch (e) {
    // Hashing is best-effort; never break the import flow because of it.
    console.warn('sha256OfFile failed:', e?.message || e);
    return null;
  }
}

/**
 * Ask the backend whether this hash matches any prior parse_job for this user.
 * Returns the raw { duplicate, match?, history_count? } payload.
 *
 * Soft check, always resolves; never throws. On network error, treats as
 * "not a duplicate" so the user is not blocked.
 */
export async function checkDuplicate({ token, file_hash, account_id, filename }) {
  if (!token || !file_hash) return { duplicate: false };
  try {
    const r = await axios.post(
      `${BACKEND_URL}/api/import/check-duplicate`,
      { file_hash, account_id: account_id || null, filename: filename || null },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 },
    );
    return r.data || { duplicate: false };
  } catch (e) {
    console.warn('checkDuplicate failed (treating as not-a-duplicate):', e?.message || e);
    return { duplicate: false };
  }
}
